import classNames from 'classnames';
import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Layer.css';

interface LayerContextValue {
  zIndex: number;
  target: HTMLElement;
}

const LayerContext = React.createContext<LayerContextValue>({
  zIndex: 0,
  target: document.body,
});

export function LayerProvider({ children }: React.PropsWithChildren) {
  const ref = useRef(null);

  return (
    <div ref={ref}>
      <LayerContext.Provider
        value={{
          zIndex: 0,
          target: ref.current || document.body,
        }}
      >
        {children}
      </LayerContext.Provider>
    </div>
  );
}

export function Layer({
  children,
  onOutsideClick,
  zIndex,
}: {
  children: React.ReactNode;
  onOutsideClick?: () => void;
  zIndex?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { zIndex: parentZIndex, target } = React.useContext(LayerContext);
  const currentZIndex = zIndex ?? parentZIndex + 100;

  const handleOutsideClick = onOutsideClick
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target !== ref.current) {
          return;
        }
        onOutsideClick?.();
      }
    : undefined;

  return createPortal(
    <LayerContext.Provider
      value={{
        zIndex: currentZIndex,
        target,
      }}
    >
      <div
        ref={ref}
        onClick={handleOutsideClick}
        className={classNames(
          styles.layer,
          handleOutsideClick && styles.clickable,
        )}
        style={{ zIndex: currentZIndex }}
      >
        <div className={styles.content}>{children}</div>
      </div>
    </LayerContext.Provider>,
    target,
  );
}
